import {
  Box,
  Card,
  Typography,
  Avatar,
  MenuItem,
  Select,
  Stack,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import { getPipelineStage } from "../../../config/pipeline.config";

export default function PipelineStageEditor({ flow, setFlow, editing }) {
  const MotionBox = motion.create(Box);

  const pastel = (hex) => {
    if (!hex) return "#e5e7eb";
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgb(${Math.round(r * 0.2 + 255 * 0.8)}, ${Math.round(
      g * 0.2 + 255 * 0.8
    )}, ${Math.round(b * 0.2 + 255 * 0.8)})`;
  };

  const dropped = flow.length >= 1 ? flow[flow.length - 1] : null;
  const won = flow.length >= 2 ? flow[flow.length - 2] : null;

  const setDroppedStage = (key) => {
    const base = flow.filter((s) => s !== key);
    setFlow([...base, key]);
  };

  const setWonStage = (key) => {
    if (!dropped) return;
    const base = flow.filter((s) => s !== key && s !== dropped);
    setFlow([...base, key, dropped]);
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        bgcolor: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 12px 36px rgba(0,0,0,0.08)",
        p: 4,
      }}
    >
      <Typography sx={{ fontSize: 20, fontWeight: 800, mb: 4 }}>
        Pipeline Timeline
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {flow.map((key, i) => {
          const stage = getPipelineStage(key);
          const bg = pastel(stage.color);
          const isWon = key === won;
          const isDropped = key === dropped;
          const showDots = !isWon && !isDropped;

          return (
            <MotionBox
              key={key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.04 }}
              sx={{ display: "flex", gap: 4, alignItems: "center" }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: 64,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: bg,
                    color: stage.color,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                  }}
                >
                  {stage.icon}
                </Avatar>

                {showDots && i < flow.length - 1 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 70,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {[1, 2, 3].map((d) => (
                      <Box
                        key={d}
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: bg,
                          opacity: 0.8,
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              <Box
                sx={{ flex: 1, display: "flex", alignItems: "center", gap: 2 }}
              >
                <Box
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: 20,
                    bgcolor: bg,
                    color: stage.color,
                    fontWeight: 800,
                  }}
                >
                  {stage.label}
                </Box>

                {isWon && (
                  <Chip
                    label="FINAL"
                    size="small"
                    sx={{
                      bgcolor: "#DCFCE7",
                      color: "#166534",
                      fontWeight: 900,
                      border: "1px solid #86EFAC",
                    }}
                  />
                )}

                {isDropped && (
                  <Chip
                    label="DROPPED"
                    size="small"
                    sx={{
                      bgcolor: "#FEE2E2",
                      color: "#991B1B",
                      fontWeight: 900,
                      border: "1px solid #FCA5A5",
                    }}
                  />
                )}
              </Box>
            </MotionBox>
          );
        })}
      </Box>

      {editing && flow.length >= 2 && (
        <Stack direction="row" spacing={3} mt={5}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 1 }}>
              Dropped Stage
            </Typography>
            <Select
              size="small"
              value={dropped || ""}
              onChange={(e) => setDroppedStage(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              {flow.map((k) => (
                <MenuItem key={k} value={k}>
                  {getPipelineStage(k).label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 1 }}>
              Won Stage
            </Typography>
            <Select
              size="small"
              disabled={!dropped}
              value={won || ""}
              onChange={(e) => setWonStage(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              {flow
                .filter((s) => s !== dropped)
                .map((k) => (
                  <MenuItem key={k} value={k}>
                    {getPipelineStage(k).label}
                  </MenuItem>
                ))}
            </Select>
          </Box>
        </Stack>
      )}
    </Card>
  );
}
